import { useCallback, memo } from 'react'
import { replace } from 'connected-react-router'

import { useMutation } from '@tanstack/react-query'

import Routes from '@tabeeb/routes'
import { useDispatch } from '@tabeeb/store'
import CircularProgressBar from '@tabeeb/modules/shared/tabeebCircularProgressBar'
import { trainingMaterialTitleFormatter } from '@tabeeb/shared/utils/text'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { useTrainingMaterial } from '@tabeeb/modules/articles/hooks'
import { useApi } from '@tabeeb/modules/shared/utils/API'
import { TrainingMaterialUpdateRequest } from '@tabeeb/contracts/microservices/swagger-article-service'

import TrainingMaterialEditorPage from '../TrainingMaterialEditorPage'

export interface TrainingMaterialEditPageProps {
  id: number
}

const TrainingMaterialEditPage: React.FC<TrainingMaterialEditPageProps> = ({ id }) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const onError = useCallback(() => {
    dispatch(
      onAddErrorNotification({ message: trainingMaterialTitleFormatter.format('Failed to load training material') })
    )
    dispatch(replace(Routes.trainingMaterials))
  }, [dispatch])

  const updateMutation = useMutation({
    mutationFn: (request: TrainingMaterialUpdateRequest) => api.trainingMaterials.updateAsync(id, request),
    onSuccess: () => {
      dispatch(
        onAddSuccessNotification({
          message: trainingMaterialTitleFormatter.format('Training material successfully updated'),
        })
      )
      dispatch(replace(Routes.trainingMaterials))
    },
    onError: () => {
      dispatch(
        onAddErrorNotification({
          message: trainingMaterialTitleFormatter.format('Failed to update training material'),
        })
      )
    },
  })

  const { loading, trainingMaterial } = useTrainingMaterial({ id, onError })
  if (loading || !trainingMaterial) {
    return <CircularProgressBar />
  }

  return (
    <TrainingMaterialEditorPage
      title={trainingMaterialTitleFormatter.format('Edit training material')}
      initial={trainingMaterial}
      onSubmit={updateMutation.mutate}
      editing
    />
  )
}

export default memo(TrainingMaterialEditPage)
